import React from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { Route, Redirect, Switch, useHistory } from 'react-router-dom';
import {
  ContextProvider,
  CustomText,
  Box,
  HStack,
  NewSelect,
  NewCheckbox,
  EmptyState,
  Loader,
  Text,
  NewTypeahead,
  Search,
  SearchProps,
} from '@carta/ink';
import pj from '../../package.json';
import convertSampleName from './helpers/convertSampleName';

import samples from './helpers/getSamples';
import { BackstopInvisible } from './components/backstop-helpers';
import ExposeTemplate from './components/ExposeTemplate';
import ExposeHeader from './components/ExposeHeader';
import DisabledExpose from './extra-exposes/DisabledExpose';
import NegativeExpose from './extra-exposes/NegativeExpose';
import InformationalExpose from './extra-exposes/InformationalExpose';
import PositiveExpose from './extra-exposes/PositiveExpose';
import NewTableBenchmarkExpose from './extra-exposes/NewTableBenchmarkExpose';
import NewTableScrollExpose from './extra-exposes/NewTableScrollExpose';
import OrnamentExpose from './extra-exposes/OrnamentExpose';
import FormComponentsExpose from './extra-exposes/FormComponentsExpose';
import DatavizExpose from './extra-exposes/DatavizExpose';

const ExposeStage = styled.div`
  ${({ theme }) =>
    theme.dark &&
    css`
      background-color: black;
    `};

  // ONLY USED FOR VISUAL REGRESSION!
  ${({ theme }) =>
    theme.expose &&
    theme.visualDebug &&
    css`
      *,
      *:before,
      *:after {
        animation: none !important;
        transition-property: none !important;
        transform: none !important;
      }
    `}
`;

class ErrorBoundary extends React.Component<Record<string, any>, { hasError: boolean; error: any }> {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <EmptyState type="page" icon="error" text="Something went wrong">
          <Text variant="monospace" align="left">
            {this.state.error.toString()}
          </Text>
        </EmptyState>
      );
    }

    return this.props.children;
  }
}

const exposeNames = Object.keys(samples).sort();

const getSample = (expose: string, slug: string) => {
  if (!samples[expose]) return null;

  return samples[expose].filter(sample => convertSampleName(sample.name) === slug).pop();
};

const Expose = () => {
  // const [darkMode, setDarkMode] = React.useState(false);
  const [visualDebug, setVisualDebug] = React.useState(false);
  const [highlightEnvs, setHighlightEnvs] = React.useState(false);

  const history = useHistory();

  /*
    React Router's "useParams" should've been used here, but I couldn't make it work.
    Regex to the rescue, I guess?
  */
  const urlMatch = history.location.pathname.match(/(?<expose>\w+)(\/(?<sample>[\w-]+))?/);
  const { expose, sample } = urlMatch?.groups || { expose: exposeNames[0], sample: '' };

  const [component, setComponent] = React.useState(expose);

  /*
    React reuses state setters if they have the same name, which happens
    often between different samples. This ensures that the sample unmounts
    entirely when switching between them, re-setting the state.
  */
  const [transitioning, setTransitioning] = React.useState(false);
  React.useEffect(() => {
    if (transitioning) {
      setTimeout(() => {
        setTransitioning(false);
      }, 100);
    }
  }, [transitioning]);

  const exposeTheme = {
    expose: true,
    // dark: darkMode,
    visualDebug,
    highlightEnvs,
  };

  return (
    <ThemeProvider theme={exposeTheme}>
      <ContextProvider>
        <ExposeHeader>
          <HStack align="distributed">
            <HStack spacing="xlarge">
              <NewTypeahead
                id="component-select"
                value={component}
                scrollable
                onChange={(_, { value, option }) => {
                  setComponent(value);
                  if (option) history.push(`/${value}`);
                }}
                options={exposeNames}
                optionLabel={option => option}
                filterOptions={v => exposeNames.filter(option => option.toLowerCase().includes(v.toLowerCase()))}
                input={p => (
                  <Search
                    {...p}
                    size="full"
                    value={String(p.value)}
                    onChange={p.onChange as SearchProps['onChange']}
                    placeholder="Search component"
                    hasClear
                  />
                )}
              />

              <NewSelect
                id="sample-select"
                value={sample}
                onChange={event => {
                  setTransitioning(true);
                  history.push(`/${expose}/${event.target.value}`);
                }}
              >
                <NewSelect.Option value="">All samples</NewSelect.Option>
                {(samples[expose] || []).map(sample => (
                  <NewSelect.Option key={sample.name} value={convertSampleName(sample.name)}>
                    {sample.name}
                  </NewSelect.Option>
                ))}
              </NewSelect>
              <NewCheckbox
                checked={visualDebug}
                id="animation-mode-trigger"
                onChange={e => setVisualDebug(e.target.checked)}
                label="Stop animations"
                width="half"
              />
              {/* <NewCheckbox
                checked={darkMode}
                id="night-mode-trigger"
                onChange={e => setDarkMode(e.target.checked)}
                label={darkMode ? 'Dark mode' : 'Light mode'}
                width="half"
              /> */}
              <NewCheckbox
                checked={highlightEnvs}
                id="highlight-docs-trigger"
                onChange={e => setHighlightEnvs(e.target.checked)}
                label={
                  <>
                    Envs
                    <Box bottom="5px" />
                    {highlightEnvs && <CustomText size="11">plum = docs, aqua = expose</CustomText>}
                  </>
                }
                width="full"
              />
            </HStack>
            <BackstopInvisible>
              <CustomText trim="none" size="small">
                Version: {pj.version}
              </CustomText>
            </BackstopInvisible>
          </HStack>
        </ExposeHeader>
        {transitioning ? (
          <Loader isLoading type="page" />
        ) : (
          <ExposeStage id="expose-stage">
            <ErrorBoundary>
              <Switch>
                <Route exact path="/Disabled" component={DisabledExpose} />
                <Route exact path="/Negative" component={NegativeExpose} />
                <Route exact path="/Positive" component={PositiveExpose} />
                <Route exact path="/Informational" component={InformationalExpose} />
                <Route exact path="/NewTableBenchmark" component={NewTableBenchmarkExpose} />
                <Route exact path="/NewTableScroll" component={NewTableScrollExpose} />
                {/* Remove once Ornament is exported */}
                <Route exact path="/Ornament" component={OrnamentExpose} />
                <Route exact path="/Dataviz" component={DatavizExpose} />
                <Route exact path="/FormComponents" component={FormComponentsExpose} />
                <Route
                  exact
                  path="/:expose/:sample"
                  render={({ match }) => {
                    const sample = getSample(match.params.expose, convertSampleName(match.params.sample));

                    if (!sample) return <Redirect to={`/${expose}`} />;
                    return <ExposeTemplate sample={sample} />;
                  }}
                />
                <Route
                  exact
                  path="/:expose"
                  render={({ match }) => {
                    const expose = samples[match.params.expose];

                    if (!expose) {
                      return <Redirect to={`/${exposeNames[0]}`} />;
                    }

                    return expose.map((sample, idx) => <ExposeTemplate key={sample.name + idx} sample={sample} />);
                  }}
                />

                <Redirect from="/" to={exposeNames[0]} />
              </Switch>
            </ErrorBoundary>
          </ExposeStage>
        )}
      </ContextProvider>
    </ThemeProvider>
  );
};

export default Expose;
